// Centralized color definitions for the application
// This file contains all hardcoded colors used throughout the application

export const colors = {
  // Status colors
  status: {
    success: {
      light: "#bbf7d0",      // Light green text
      medium: "#22c55e",     // Medium green
      dark: "#16a34a",       // Dark green
      background: "rgba(34, 197, 94, 0.2)",  // Light green background
      border: "rgba(34, 197, 94, 0.5)",       // Green border
    },
    inactive: {
      light: "#d1d5db",      // Light gray text
      medium: "#9ca3af",     // Medium gray
      dark: "#6b7280",       // Dark gray
      background: "rgba(156, 163, 175, 0.2)", // Light gray background
      border: "rgba(156, 163, 175, 0.5)",      // Gray border
    },
    error: {
      light: "#fecaca",      // Light red text
      medium: "#ef4444",     // Medium red
      dark: "#dc2626",       // Dark red
      background: "rgba(220, 38, 38, 0.1)",   // Light red background
      border: "rgba(220, 38, 38, 0.3)",       // Red border
    },
    warning: {
      light: "#fde68a",      // Light yellow text
      medium: "#f59e0b",     // Medium yellow
      dark: "#d97706",       // Dark yellow
      background: "rgba(245, 158, 11, 0.2)",  // Light yellow background
      border: "rgba(245, 158, 11, 0.5)",      // Yellow border
    },
    appointment: {
      confirmed: "#28a745",  // Green for confirmed
      pending: "#ffc107",    // Yellow for pending
      cancelled: "#dc3545",  // Red for cancelled
      completed: "#17a2b8",  // Blue for completed
      background: {
        confirmed: "rgba(40, 167, 69, 0.2)",
        pending: "rgba(255, 193, 7, 0.2)",
        cancelled: "rgba(220, 53, 69, 0.2)",
        completed: "rgba(23, 162, 184, 0.2)",
      },
    },
  },

  // Table colors
  table: {
    header: {
      background: "rgba(255, 255, 255, 0.1)",
      cellBackground: {
        dark: "rgba(255, 255, 255, 0.05)",
        light: "rgba(255, 255, 255, 1)",
      },
    },
    row: {
      even: "rgba(30, 41, 59, 0.3)",
      odd: "rgba(30, 41, 59, 0.5)",
    },
    tableRow: {
      even: "rgba(75, 85, 99, 0.3)",
      odd: "rgba(75, 85, 99, 0.5)",
    },
  },

  // Shadow colors
  shadows: {
    light: "rgba(0, 0, 0, 0.2)",
    medium: "rgba(0, 0, 0, 0.3)",
    heavy: "rgba(0, 0, 0, 0.5)",
    card: "0 8px 32px rgba(0, 0, 0, 0.2)",
    text: "0 2px 4px rgba(0, 0, 0, 0.3)",
  },

  // Action colors
  actions: {
    edit: "#3b82f6",        // Blue for edit
    delete: "#ef4444",      // Red for delete
    add: "#10b981",         // Green for add
    view: "#8b5cf6",        // Purple for view
  },

  // Common UI colors
  ui: {
    background: {
      light: "rgba(255, 255, 255, 1)",
      medium: "rgba(255, 255, 255, 0.8)",
      dark: "rgba(255, 255, 255, 0.05)",
    },
    border: {
      light: "rgba(0, 0, 0, 0.1)",
      medium: "rgba(0, 0, 0, 0.2)",
      dark: "rgba(0, 0, 0, 0.3)",
    },
  },

  // Calendar colors
  calendar: {
    background: {
      main: "rgba(255, 255, 255, 0.05)",
      header: "rgba(255, 255, 255, 0.1)",
      cell: "rgba(255, 255, 255, 0.02)",
      selected: "rgba(255, 255, 255, 0.15)",
    },
    border: {
      light: "rgba(255, 255, 255, 0.1)",
      medium: "rgba(255, 255, 255, 0.15)",
      strong: "rgba(255, 255, 255, 0.2)",
      hover: "rgba(255, 255, 255, 0.3)",
    },
    text: {
      primary: "rgba(255, 255, 255, 0.9)",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    button: {
      inactive: {
        background: "rgba(255, 255, 255, 0.1)",
        border: "rgba(255, 255, 255, 0.3)",
        color: "rgba(255, 255, 255, 0.7)",
      },
      active: {
        background: "rgba(255, 255, 255, 0.2)",
        border: "rgba(255, 255, 255, 0.5)",
        color: "rgba(255, 255, 255, 1)",
      },
      hover: {
        background: "rgba(255, 255, 255, 0.15)",
        border: "rgba(255, 255, 255, 0.4)",
        color: "rgba(255, 255, 255, 0.8)",
      },
    },
  },
};

// Helper function to get status colors based on condition
export const getStatusColors = (isActive) => {
  return isActive ? colors.status.success : colors.status.inactive;
};

// Helper function to get table row background based on index
export const getTableRowBackground = (index, isDarkMode = true) => {
  if (isDarkMode) {
    return index % 2 === 0 ? colors.table.row.even : colors.table.row.odd;
  } else {
    return index % 2 === 0 ? '#ffffff' : '#fefefe';
  }
};

// Helper function to get actual table row background (for tables only)
export const getActualTableRowBackground = (index, isDarkMode = true) => {
  if (isDarkMode) {
    return index % 2 === 0 ? colors.table.tableRow.even : colors.table.tableRow.odd;
  } else {
    return index % 2 === 0 ? '#ffffff' : '#fefefe';
  }
};

// Helper function to get calendar colors based on theme
export const getCalendarColors = (isDarkMode = true) => {
  if (isDarkMode) {
    return colors.calendar;
  } else {
    return {
      background: {
        main: "#f1f3f4",
        header: "#e8eaed",
        cell: "#fafbfc",
        selected: "#d2e3fc",
      },
      border: {
        light: "rgba(0, 0, 0, 0.15)",
        medium: "rgba(0, 0, 0, 0.25)",
        strong: "rgba(0, 0, 0, 0.35)",
        hover: "rgba(0, 0, 0, 0.4)",
      },
      text: {
        primary: "rgba(0, 0, 0, 0.9)",
        secondary: "rgba(0, 0, 0, 0.7)",
      },
      button: {
        inactive: {
          background: "rgba(0, 0, 0, 0.08)",
          border: "rgba(0, 0, 0, 0.25)",
          color: "rgba(0, 0, 0, 0.7)",
        },
        active: {
          background: "rgba(0, 0, 0, 0.15)",
          border: "rgba(0, 0, 0, 0.4)",
          color: "rgba(0, 0, 0, 0.95)",
        },
        hover: {
          background: "rgba(0, 0, 0, 0.12)",
          border: "rgba(0, 0, 0, 0.35)",
          color: "rgba(0, 0, 0, 0.85)",
        },
      },
    };
  }
};

// Helper function to get table header styles
export const getTableHeaderStyles = (isDarkMode) => ({
  background: colors.table.header.background,
  cellBackground: isDarkMode 
    ? colors.table.header.cellBackground.dark 
    : colors.table.header.cellBackground.light,
});

export default colors;
